import { useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * IBAN/BIC entry form for artisans.
 * Displayed on the Dashboard until they enter their bank details.
 * Once entered, allows viewing (masked) and editing.
 */
export default function BankDetailsForm() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(!user?.iban);
  const [iban, setIban] = useState(user?.iban || "");
  const [bic, setBic] = useState(user?.bic || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.patch("/users/me", {
        iban: iban.trim(),
        bic: bic.trim() || null,
      });
      toast.success("Coordonnées bancaires enregistrées ✅");
      try { await refresh?.(); } catch (_e) { /* ignore */ }
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const hasIban = !!user?.iban;
  const masked = hasIban
    ? `${user.iban.slice(0, 4)} •••• •••• •••• ${user.iban.slice(-4)}`
    : null;

  if (!open && hasIban) {
    return (
      <div
        className="mb-8 border-2 border-emerald-700 bg-emerald-50 p-4 flex flex-wrap items-center gap-3"
        data-testid="bank-details-summary"
      >
        <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="display text-lg text-emerald-900">Coordonnées bancaires enregistrées</div>
          <div className="mono text-[10px] uppercase tracking-widest text-emerald-800 mt-0.5">
            IBAN : <span className="text-emerald-950">{masked}</span> · Prêt à recevoir vos paiements séquestrés
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="mono text-[10px] uppercase tracking-widest border border-emerald-700 text-emerald-900 px-3 py-1.5 hover:bg-emerald-700 hover:text-white transition"
          data-testid="bank-details-edit"
        >
          Modifier
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mb-8 border-2 border-[#FF4D00] bg-[#FFF7ED] p-6"
      data-testid="bank-details-form"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center shrink-0">
          <Landmark className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Coordonnées bancaires</div>
          <div className="display text-xl mt-1">Renseignez votre RIB pour recevoir vos paiements</div>
          <p className="text-sm text-zinc-700 mt-1">
            Sans IBAN valide, vos fonds séquestrés ne pourront pas vous être virés après validation du chantier.
            Vos données sont chiffrées et jamais partagées.
          </p>

          <div className="mt-5 grid sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-2">
              <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">IBAN * (FR76 xxxx xxxx xxxx xxxx xxxx xxx)</span>
              <input
                type="text"
                required
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                placeholder="FR76 3000 4028 8000 0123 4567 890"
                className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 mono text-sm focus:outline-none focus:bg-[#FFF7ED]"
                data-testid="bank-details-iban"
              />
            </label>
            <label className="block">
              <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">BIC / SWIFT (facultatif)</span>
              <input
                type="text"
                value={bic}
                onChange={(e) => setBic(e.target.value)}
                placeholder="BNPAFRPP"
                className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 mono text-sm focus:outline-none focus:bg-[#FFF7ED]"
                data-testid="bank-details-bic"
              />
            </label>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              type="submit"
              disabled={busy}
              className="btn-batisur flex items-center gap-2"
              data-testid="bank-details-submit"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Enregistrement..." : "Enregistrer mon RIB"}
            </button>
            {hasIban && (
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#0A0A0B]"
                data-testid="bank-details-cancel"
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
